import { ReactElement, useEffect, useRef, useState } from "react";

import {
  Heading,
  Container as ChakraContainer,
  Flex,
  Image as ChakraImage,
  Stack,
  Box,
  VStack,
  useColorModeValue,
  SimpleGrid,
  Skeleton,
} from "@chakra-ui/react";

import MainIndex from "components/layout/main-index";
import Container from "components/main-container";

import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Virtual, Navigation, Pagination } from "swiper";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";

import { GraphQLQuery } from "@aws-amplify/api";
import {
  CardCollectionLaunchpad,
  CardCollectionLaunchpadSkeleton,
  ContainerCollection,
} from "components/global/card-collection";
import { API } from "aws-amplify";
import { Collection, CollectionByStatusQuery, CollectionStatus } from "src/API";
import { collectionByStatus } from "src/graphql/queries";

interface CollectionsProps {
  loaded: boolean;
  collections: Collection[];
}

const LaunchpadPage = () => {
  const swiperPageColor = useColorModeValue("gray.300", "main.spaceCadet");
  const swiperPageOverlay = useColorModeValue(
    "blackAlpha.500",
    "blackAlpha.600"
  );
  const swiperRef = useRef<SwiperCore>(null) as any;
  const [swiperIndex, setSwiperIndex] = useState<number>(0);

  const [collectionsComingSoon, setCollectionsComingSoon] =
    useState<CollectionsProps>({
      loaded: false,
      collections: [],
    });
  const [collectionsLive, setCollectionsLive] = useState<CollectionsProps>({
    loaded: false,
    collections: [],
  });
  const [collectionsEnded, setCollectionsEnded] = useState<CollectionsProps>({
    loaded: false,
    collections: [],
  });

  const slideTo = (index: number) => {
    if (swiperRef.current) {
      swiperRef.current.swiper.slideTo(index);
    }
  };

  const swiperCollection: Collection[] = [
    ...collectionsLive.collections
      .filter((f) => f.bannerUrl && f.thumbnailUrl && f.description)
      .slice(0, 2),
    ...collectionsComingSoon.collections
      .filter((f) => f.bannerUrl && f.thumbnailUrl && f.description)
      .slice(0, 2),
  ];

  useEffect(() => {
    const getListCollectionsByStatus = async (status: CollectionStatus) => {
      const collection = await API.graphql<
        GraphQLQuery<CollectionByStatusQuery>
      >({
        query: collectionByStatus,
        variables: {
          status: status,
        },
      });
      return collection;
    };

    try {
      getListCollectionsByStatus(CollectionStatus.COMING_SOON)
        .then((res) => {
          if (res.data?.collectionByStatus?.items) {
            setCollectionsComingSoon((c) => ({
              loaded: true,
              collections: [
                ...(res.data?.collectionByStatus?.items as Collection[]),
              ],
            }));
          } else
            setCollectionsComingSoon((c) => ({
              loaded: false,
              collections: [],
            }));
        })
        .catch((error) => console.log(error));

      getListCollectionsByStatus(CollectionStatus.AVAILABLE)
        .then((res) => {
          if (res.data?.collectionByStatus?.items) {
            setCollectionsLive((c) => ({
              loaded: true,
              collections: [
                ...(res.data?.collectionByStatus?.items as Collection[]),
              ],
            }));
          } else
            setCollectionsLive((c) => ({ loaded: false, collections: [] }));
        })
        .catch((error) => console.log(error));

      getListCollectionsByStatus(CollectionStatus.ENDED)
        .then((res) => {
          if (res.data?.collectionByStatus?.items) {
            setCollectionsEnded((c) => ({
              loaded: true,
              collections: [
                ...c.collections,
                ...(res.data?.collectionByStatus?.items as Collection[]),
              ],
            }));
          } else
            setCollectionsEnded((c) => ({ loaded: false, collections: [] }));
        })
        .catch((error) => console.log(error));

      getListCollectionsByStatus(CollectionStatus.SOLD_OUT)
        .then((res) => {
          if (res.data?.collectionByStatus?.items) {
            setCollectionsEnded((c) => ({
              loaded: true,
              collections: [
                ...c.collections,
                ...(res.data?.collectionByStatus?.items as Collection[]),
              ],
            }));
          } else
            setCollectionsEnded((c) => ({ loaded: false, collections: [] }));
        })
        .catch((error) => console.log(error));
    } catch (error) {
      console.log(error);
    }
  }, []);

  return (
    <Container title={"Launchpad"} maxW="full" p={0}>
      <Box position={"relative"} overflow={"hidden"}>
        {collectionsComingSoon.loaded && collectionsLive.loaded ? (
          <>
            <Box
              position={"absolute"}
              w={"full"}
              h={"full"}
              left={0}
              top={0}
              bgImage={
                swiperCollection[swiperIndex].bannerUrl
                  ? `url('${swiperCollection[swiperIndex].bannerUrl}')`
                  : `url('/images/default-image/default-banner-1.webp')`
              }
              bgSize={"cover"}
              bgPosition={"center"}
              filter={"blur(20px)"}
              transitionProperty={"background-image"}
              transitionDuration={".3s"}
              transitionTimingFunction={"cubic-bezier(.4, 0, .2, 1)"}
              willChange={"transform"}
            />
            <Box
              position={"absolute"}
              w={"full"}
              h={"full"}
              bg={swiperPageOverlay}
            />

            <Swiper
              onInit={(core: SwiperCore) => {
                swiperRef.current = core.el;
              }}
              onUpdate={(swiper) => {
                setSwiperIndex(swiper.realIndex);
              }}
              onSlideChange={(swiper) => {
                setSwiperIndex(swiper.realIndex);
              }}
              modules={[Virtual, Navigation, Pagination]}
              virtual
              grabCursor
            >
              {collectionsComingSoon.loaded && collectionsLive.loaded ? (
                swiperCollection.map((collection, index) => (
                  <SwiperSlide key={index}>
                    <ContainerCollection collection={collection} />
                  </SwiperSlide>
                ))
              ) : (
                <SwiperSlide>
                  <Skeleton w={"full"} h={{ base: "550px", md: "550px" }} />
                </SwiperSlide>
              )}
            </Swiper>
          </>
        ) : (
          <Skeleton w={"full"} h={{ base: "550px", md: "550px" }} />
        )}
      </Box>

      <VStack my={6}>
        <Flex gap={4} w={"50%"} justifyContent={"center"}>
          {collectionsComingSoon.loaded && collectionsLive.loaded
            ? swiperCollection.map((tab, index) => (
                <Box
                  key={index}
                  bg={index == swiperIndex ? "main.magenta" : swiperPageColor}
                  h={3}
                  w={"30%"}
                  cursor={"pointer"}
                  onClick={() => slideTo(index)}
                />
              ))
            : [...Array(4)].map((tab, index) => (
                <Skeleton key={index} w={"30%"} h={3} />
              ))}
        </Flex>
      </VStack>
      <ChakraContainer maxW={"container.xl"} my={24}>
        <Stack gap={24}>
          <Stack
            gap={8}
            hidden={
              collectionsLive.loaded && collectionsLive.collections.length === 0
            }
          >
            <Heading as={"h3"} size={"lg"}>
              LIVE NOW
            </Heading>
            <SimpleGrid
              templateColumns={{
                base: "repeat(auto-fill,minmax(200px,1fr))",
                md: "repeat(auto-fill,minmax(240px,1fr))",
              }}
              spacing={5}
            >
              {collectionsLive.loaded
                ? collectionsLive.collections.map((collection, index) => (
                    <CardCollectionLaunchpad
                      key={index}
                      collection={collection}
                    />
                  ))
                : [...Array(4)].map((i, index) => (
                    <CardCollectionLaunchpadSkeleton key={index} />
                  ))}
            </SimpleGrid>
          </Stack>
          <Stack
            gap={8}
            hidden={
              collectionsComingSoon.loaded &&
              collectionsComingSoon.collections.length === 0
            }
          >
            <Heading as={"h3"} size={"lg"}>
              COMING SOON
            </Heading>
            <SimpleGrid
              templateColumns={{
                base: "repeat(auto-fill,minmax(200px,1fr))",
                md: "repeat(auto-fill,minmax(240px,1fr))",
              }}
              spacing={5}
            >
              {collectionsComingSoon.loaded
                ? collectionsComingSoon.collections.map((collection, index) => (
                    <CardCollectionLaunchpad
                      key={index}
                      collection={collection}
                    />
                  ))
                : [...Array(4)].map((i, index) => (
                    <CardCollectionLaunchpadSkeleton key={index} />
                  ))}
            </SimpleGrid>
          </Stack>
          <Stack
            gap={8}
            hidden={
              collectionsEnded.loaded &&
              collectionsEnded.collections.length === 0
            }
          >
            <Heading as={"h3"} size={"lg"}>
              ENDED
            </Heading>
            <SimpleGrid
              templateColumns={{
                base: "repeat(auto-fill,minmax(200px,1fr))",
                md: "repeat(auto-fill,minmax(240px,1fr))",
              }}
              spacing={5}
            >
              {collectionsEnded.loaded
                ? collectionsEnded.collections.map((collection, index) => (
                    <CardCollectionLaunchpad
                      key={index}
                      collection={collection}
                    />
                  ))
                : [...Array(4)].map((i, index) => (
                    <CardCollectionLaunchpadSkeleton key={index} />
                  ))}
            </SimpleGrid>
          </Stack>
        </Stack>
      </ChakraContainer>
    </Container>
  );
};

LaunchpadPage.getLayout = (page: ReactElement) => {
  return (
    <>
      <MainIndex>{page}</MainIndex>
    </>
  );
};

export default LaunchpadPage;
